// React
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";

// React Query
import { useQuery, useQueryClient } from "@tanstack/react-query";

// Third Party
import moment from "moment";

// Custom Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";

// Components
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";

// Helpers
import { and, or } from "@/Common/helpers/sonar";
import { copyObjectSetup, createNewItem } from "@/Motivators/helpers";

// Constants
import { ID_NEW_SETUP } from "@/Motivators/constants";
import { extractLeafKeys } from "@/Common/GenericTree/utils";
import { useLocationCycles } from "@/Washouts/hooks/common/useLocationCycles";

export default function MotivatorsSidebar({ callbackSelected, selected, callbackAdd, setOpenNewListDialog, masterEdit }) {
	const { useAuth } = useAuthorization();
	const [commissionCycleItem, setCommissionCycleItem] = useState(null);
	const { getSubscription, deleteSubscription, addSubscription, clearSubscriptions } = useSubscriptions();
	const {
		locationId,
		motivatorType,
		actions,
		subscriptions,
		queries,
		helpers,
		identifiers,
		subscriptionReducer,
		selection,
		setSelection,
		isLoading,
		setIsLoading,
		selectNewlyCreated,
		setSelectNewlyCreated,
	} = useMotivators();
	const QUERY_IDENTIFIERS = [motivatorType + "Sidebar"];
	const { clearCache } = useCacheCleaner();
	const hasPermission = useAuth(actions.editable);
	const hasAutoSelectedDefault = useRef(false);

	const { data: commissionCycles } = useLocationCycles();

	const isGenericTreeIntegrated = (motivatorType === "spiffSetup" || motivatorType === "spiffBonusSetup");

	/******************************************************** QUERIES ********************************************************/
	const queryClient = useQueryClient();
	const { data, isLoading: isLoadingTree } = useQuery({
		queryKey: [motivatorType + "Sidebar", locationId],
		queryFn: async () =>
			queries.getSideBarData({
				locationId,
				department: identifiers.department,
			}),
		enabled: Boolean(locationId),
		staleTime: Infinity,
	});

	/******************************************************** HANDLERS ********************************************************/
	const handleSelection = (element) => {
		if (element) {
			const isActualCycle = Boolean(
				element?.status === "ACTIVE" ||
					element?.status === "CLOSING" ||
					element?.status === "POSTED" ||
					element?.status === "STAGING"
			);
			setCommissionCycleItem(element);
			callbackSelected?.(element, isActualCycle);
		}
	};

	// Tree Generation
	const sideBarTree = useMemo(() => {
		if (!data) {
			return [];
		}
		return helpers.generateTree({ data, locationId, hasPermission, masterEdit });
	}, [data, locationId]);

	const defaultSelected = useMemo(() => {
		if (!data || !helpers.getDefaultSelection) {
			return null;
		}
		return helpers.getDefaultSelection({ data });
	}, [data, helpers]);

	const defaultExpanded = useMemo(() => {
		if (!data || !helpers.getExpandedIds) {
			return [];
		}
		return helpers.getExpandedIds({ data });
	}, [data, helpers]);

	useEffect(() => {
		if (!data || !helpers.prefetchData) {
			return;
		}
		helpers
			.prefetchData({
				data,
				locationId,
				department: identifiers.department,
			})
			.then(({ cacheUpdates = [], defaultSelection }) => {
				cacheUpdates.forEach(({ queryKey, data: cacheData }) => {
					queryClient.setQueryData(queryKey, cacheData);
				});

				if (!hasAutoSelectedDefault.current && defaultSelection) {
					handleSelection(defaultSelection);
					hasAutoSelectedDefault.current = true;
				}
			})
			.catch((error) => {
				console.error("Error prefetching data:", error);
			});
	}, [data, locationId, identifiers.department]);

	function findCommissionCycle(creationDate) {
		const creationMoment = moment(creationDate);

		return (
			commissionCycles?.find((cycle) => {
				const startMoment = moment(cycle.startDate);
				const endMoment = moment(cycle.endDate);

				return startMoment.isSameOrBefore(creationMoment) && endMoment.isSameOrAfter(creationMoment);
			}) || null
		);
	}

	const subscribeToTree = (params, subscriptionsObj, subscriptionKey) => {
		const filteredSubscription = Array.from(subscriptionsObj.entries()).filter(([_, eventQuery]) => eventQuery.query);
		const resolvedSubscriptions = filteredSubscription.map(([event, eventQuery]) => {
			return {
				context: event,
				query: eventQuery.query,
				variables: {
					locationId: params?.locationId,
					department: params?.department,
					_id: params?._id,
					commissionCycleId: params?.commissionCycleId,
				},
			};
		});
		if (subscriptionKey.includes("Sidebar")) {
			addSubscription([subscriptionKey, params?.locationId], resolvedSubscriptions);
		} else {
			addSubscription([subscriptionKey, params?.locationId, params?._id], resolvedSubscriptions);
		}
	};

	const selectNewlyCreatedSetup = (setup) => {
		const idPrefix = "null_";
		const setupId = setup._id;
		let selectedId = setupId;
		const regexPattern =
			/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}_[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

		if (!selectedId?.startsWith(idPrefix) && !regexPattern.test(selectedId)) {
			selectedId = `${idPrefix}${setupId}`;
		}

		const commissionCycle = findCommissionCycle(selection?.commissionCycleStartDate);

		handleSelection({
			commissionCycleStatus: commissionCycle?.status,
			commissionCycleId: commissionCycle?._id,
			commissionCycleStartDate: commissionCycle?.startDate,
			commissionCycleEndDate: commissionCycle?.endDate,
			...setup,
			setupId,
			_id: selectedId,
			...(isGenericTreeIntegrated ? { _id: `${commissionCycle?._id}_${setupId}` } : {}),
			...(motivatorType === "specialUnitsSetup" ? { specialUnitsListId: setupId } : {}),
		});
	};

	const handleMotivatorType = async ({ value, response }) => {
		switch (motivatorType) {
			case "spiffSetup":
			case "autoAlertsSetup":
			case "spiffBonusSetup":
			case "specialUnitsSetup":
			case "salesTemporaryGuaranteed":
			case "temporaryGuaranteed":
				if (value === "onCreateSetup" && selectNewlyCreated) {
					const setup = response?.data?.[subscriptions.get(value)?.getter];					
					selectNewlyCreatedSetup(setup);
					setIsLoading(false);
					setSelectNewlyCreated(false);
					setOpenNewListDialog?.(false);
				}
				break;
			default:
				break;
		}
	};

	/******************************************************** SUBSCRIPTION ********************************************************/
	useMemo(() => {
		const sidebarSubscriptions = getSubscription([motivatorType + "Sidebar", locationId]);
		Array.from(subscriptions.entries()).forEach(([value]) => {
			sidebarSubscriptions?.subscriptions?.[value]?.setCallback(async (response) => {
				if (!locationId) {
					return;
				}

				if (isSubscriptionOfAwardsForSetup(motivatorType, value)) {
					updateAwardsOnSpiffDefinition(response);
					return;
				}

				let allItems = await queries.getSideBarData({
					locationId,
					department: identifiers.department,
				});

				const isSavingItem = !!selection?.saving;
				const isSavedItem = selection?._id !== `null_${ID_NEW_SETUP}`;

				if (isSavingItem) {
					await handleMotivatorType({
						value,
						response,
					});
				} else if (!isSavedItem) {
					const copySetup = copyObjectSetup(motivatorType, selection);

					allItems = createNewItem({
						tree: allItems,
						ccId: selection.commissionCycleId,
						newItem: copySetup,
						param: identifiers.attribute,
					});
				}

				if (value === "onUpdateSetup") {
					const updatedSetup = response?.data?.[subscriptions.get(value)?.getter];
					helpers.updateSelectedItem?.({
						cachedSidebarData: allItems,
						identifiers,
						selection: { ...selection, ...updatedSetup, _id: selection._id },
						setSelection: callbackSelected,
					});
				}

				queryClient.setQueryData([`${motivatorType}Sidebar`, locationId], allItems);

				checkIfDeleteSetup(value);
				setIsLoading(false);
			});
		});
	}, [
		getSubscription,
		locationId,
		queryClient,
		subscriptions,
		queries,
		motivatorType,
		setIsLoading,
		setSelection,
		helpers,
		identifiers,
		selection,
		selectNewlyCreated,
		setSelectNewlyCreated,
	]);

	function updateAwardsOnSpiffDefinition(response) {
		const { onCreateAward = {}, onUpdateAward = {}, onDeleteAward = {} } = response.data;

		queryClient.setQueryData([`${motivatorType}Entry`, selection?._id, locationId], (oldCached) => {
			if (onCreateAward?._id && onCreateAward?.items) {
				return actionAwardsToDefinition(onCreateAward?.items, oldCached, "add");
			} else if (onUpdateAward?._id && onUpdateAward?.items) {
				return actionAwardsToDefinition(onUpdateAward?.items, oldCached, "update");
			} else if (onDeleteAward?._id && onDeleteAward?.items) {
				return actionAwardsToDefinition(onDeleteAward?.items, oldCached, "delete");
			}

			return oldCached;
		});
	}

	function isSubscriptionOfAwardsForSetup(motType, value) {
		const sales = motType === "spiffSetup" && or(value === "onCreateSubItem", value === "onUpdateSubItem", value === "onDeleteSubItem");
		const service =
			motType === "spiffBonusSetup" && or(value === "onCreateSubItem", value === "onUpdateSubItem", value === "onDeleteSubItem");
		return or(sales, service);
	}

	function actionAwardsToDefinition(items, oldCached, action) {
		const [modifiedDef = {}] = items;
		const { _id: defId = "", awards = [] } = modifiedDef;

		const newDefinitions = [...oldCached.items].map((def) => {
			if (and(def._id === defId, awards?.length)) {
				const newAwards = [...def.awards];

				const [{ _id, spiffDate }] = awards;

				if (action === "add" && awards.length === 1) {
					return { ...def, awards: [...newAwards, { _id, spiffDate }] };
				} else if (action === "update") {
					return {
						...def,
						awards: newAwards.map((award) => {
							if (award._id === _id) {
								return { ...award, spiffDate };
							}
							return award;
						}),
					};
				} else if (action === "delete") {
					const deletedIds = awards.map((a) => a._id);
					return { ...def, awards: newAwards.filter((a) => !deletedIds.includes(a._id)) };
				}

				return { ...def, awards: newAwards };
			}

			return def;
		});

		return { ...oldCached, items: newDefinitions };
	}

	const checkIfDeleteSetup = (value) => {
		if (value === "onDeleteSetup") {
			setSelection(null);
		}
	};

	useEffect(() => {
		hasAutoSelectedDefault.current = false;
		subscribeToTree({ locationId, department: identifiers?.department }, subscriptions, motivatorType + "Sidebar");

		return () => {
			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptions();
		};
	}, [locationId, subscriptionReducer]);

	return (
		<AppSideBar
			searchProps={{
				loading: isLoadingTree,
			}}
			sideBarProps={{
				tree: sideBarTree,
				loading: isLoadingTree,
				selected: defaultSelected || selection?._id,
				expanded: defaultExpanded,
				disabled: isLoading,
				onSelectAction: handleSelection,
				onControlledClickAction: (item) => {
					if (isGenericTreeIntegrated) {
						const [commissionCycleId] = extractLeafKeys(item._id);						
						callbackAdd({ commissionCycleId });
					} else {
						const { commissionCycleId, commissionCycleEndDate, commissionCycleStartDate } = item || {};
						setCommissionCycleItem(item);
						callbackAdd({ commissionCycleId, commissionCycleEndDate, commissionCycleStartDate });
					}
				},
			}}
		/>
	);
}

MotivatorsSidebar.propTypes = {
	setOpenNewListDialog: PropTypes.func,
	selected: PropTypes.object,
	callbackSelected: PropTypes.func,
	callbackAdd: PropTypes.func,
	lastSelected: PropTypes.shape({
		current: PropTypes.object,
	}),
	masterEdit: PropTypes.shape({
		SALES: PropTypes.bool,
		SERVICE: PropTypes.bool,
	}),
};
